import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostModalContext } from '@/contexts/usePostModalContext';
import { usePhotoModalContextOptional } from '@/contexts/usePhotoModalContext';
import type { NotificationTarget } from '@/lib/notification-target';

/**
 * Shared hook for notification navigation target handling.
 * Used by both NotificationItem (dropdown) and NotificationPageItem (page).
 * Dispatches to post modal, photo modal, or router navigate based on target type.
 */
export function useNotificationNavigation() {
  const navigate = useNavigate();
  const { openModalInPlace } = usePostModalContext();
  const photoModalContext = usePhotoModalContextOptional();

  const handleNavigationTarget = useCallback((target: NotificationTarget) => {
    const getCommentOptions = () => {
      if (!target.commentId) return undefined;
      return target.isReply
        ? { replyId: target.commentId, parentCommentId: target.parentCommentId }
        : { commentId: target.commentId };
    };

    switch (target.type) {
      case 'post':
        if (target.username && target.postId) {
          openModalInPlace(target.username, target.postId, getCommentOptions());
        }
        break;
      case 'photo':
        if (target.username && target.postId && target.photoId && photoModalContext) {
          photoModalContext.openModalInPlace(
            target.username, target.photoId, target.postId, getCommentOptions()
          );
        } else if (target.username && target.postId) {
          openModalInPlace(target.username, target.postId, getCommentOptions());
        }
        break;
      case 'profile':
      case 'circle':
        if (target.route) {
          navigate(target.route);
        }
        break;
    }
  }, [openModalInPlace, photoModalContext, navigate]);

  return { handleNavigationTarget };
}
